<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.type} input-${size}`"
    :data-type="context.type"
  >
    <input
      type="file"
      ref="fileInput"
      class="d-none"
      :accept="context.attributes.accept || '*'"
      @change="handleFileChange"
    />

    <div
      class="mc-file-input-container"
      :class="{ 'is-invalid': fileError }"
      @click="triggerFileInput"
    >
      <div class="mc-file-input-button">
        {{ buttonText || 'Pilih dokumen' }}
      </div>

      <div class="mc-file-input-text" :class="{ 'has-file': fileName }">
        <a v-if="previewUrl" href="#" @click.stop.prevent="openPreview">
          {{ fileName }}
        </a>

        <span v-else>
          {{ fileName || context.attributes.placeholder || 'Tidak ada dokumen terpilih' }}
        </span>
      </div>
    </div>

    <div v-if="computedHelpText" class="mc-file-input-help">
      {{ computedHelpText }}
    </div>

    <div v-if="fileError" class="mc-file-input-error">
      {{ fileError }}
    </div>

    <!-- Preview -->
    <div v-if="showPreview" class="mc-file-preview-wrapper mt-3">
      <div class="mc-file-preview-item">
        <div class="mc-file-preview-img" @click="handleDownload">
          <img v-if="isImage" :src="previewUrl" :alt="fileName" />

          <div v-else class="mc-file-preview-icon">
            <i class="icon-outline_file-text"></i>
          </div>

          <button class="mc-file-preview-download" @click.stop="handleDownload">
            <i class="icon-outline_download"></i>
          </button>
        </div>

        <p class="mc-file-preview-name">{{ fileName }}</p>
      </div>
    </div>
    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="attachments"
      :activeAttachmentIdx.sync="activeAttachmentIdx"
      @handleDownloadImg="handleDownloadImg"
    />
  </div>
</template>

<script>
import PreviewImage from './components/PreviewImage.vue'

export default {
  components: {
    PreviewImage,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    buttonText: {
      type: String,
      default: '',
    },
    helpText: {
      type: String,
      default: '',
    },
    onFileChange: {
      type: Function,
      default: () => {},
    },
    maxSize: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      fileName: '',
      previewUrl: '',
      fileError: '',
      activeAttachmentIdx: null,
    }
  },
  computed: {
    isImage() {
      if (!this.fileName) return false
      const ext = this.fileName.split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    },
    showPreview() {
      return false
    },
    computedHelpText() {
      if (this.helpText) return this.helpText

      const formats = this.context.attributes.accept
        ? this.context.attributes.accept
            .split(',')
            .map((f) => f.replace('.', '').trim().toUpperCase())
            .join(', ')
        : 'JPG, JPEG, PNG, PDF'

      let text = `Format: ${formats}`
      if (this.maxSize) {
        text += ` • Maksimal ${this.maxSize} MB`
      }

      return text
    },
    attachments() {
      if (!this.previewUrl) return []

      return [
        {
          url: this.previewUrl,
          name: this.fileName
        }
      ]
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.context.visibleValidationErrors = []
      if (this.maxSize) {
        const maxBytes = this.maxSize * 1024 * 1024

        if (file.size > maxBytes) {
          this.fileError = `Ukuran file maksimal ${this.maxSize} MB`
          event.target.value = ''
          return
        }
      }

      if (this.context.attributes.accept) {
        const allowedFormats = this.context.attributes.accept
          .split(',')
          .map((ext) => ext.trim().toLowerCase())

        const fileExt = '.' + file.name.split('.').pop().toLowerCase()

        if (!allowedFormats.includes(fileExt)) {
          this.fileError = `Format file tidak didukung`
          event.target.value = ''
          return
        }
      }

      const reader = new FileReader()

      reader.onload = (e) => {
        const base64 = e.target.result
        const base64Only = base64.split(',')[1]
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "")

        const attachment = {
          file_name: fileNameWithoutExt,
          file_type: file.type,
          file_size: file.size,
          file_base64: base64Only
        }

        this.context.model = attachment

        this.fileName = file.name
        this.previewUrl = URL.createObjectURL(file)

        this.onFileChange(attachment)
      }

      reader.readAsDataURL(file)
    },
    handleDownload() {
      if (this.previewUrl) {
        window.open(this.previewUrl, '_blank')
      }
    },
    openPreview() {
      this.activeAttachmentIdx = 0
    },
    handleDownloadImg(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
    },
  },
}
</script>

<style lang="scss" scoped>
.formulate-input-element--file-input {
  flex-direction: column;
}

.mc-file-input-container {
  display: flex;
  width: 100%;
  border: 1px solid #99a1b1;
  border-radius: 4px;
  height: 38px;
  cursor: pointer;
  overflow: hidden;
  background-color: #fff;

  &.is-invalid {
    border-color: #dc3545;
  }

  &:hover {
    border-color: #0056b3;
  }
}

.mc-file-input-button {
  padding: 0 16px;
  display: flex;
  align-items: center;
  border-right: 1px solid #99a1b1;
  color: #0056b3;
  font-weight: 500;
  white-space: nowrap;
  font-size: 14px;
  background-color: #fff;
}

.mc-file-input-text {
  padding: 0 12px;
  display: flex;
  align-items: center;
  color: #99a1b1;
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;

  &.has-file {
    color: #344264;

    a {
      color: #0056b3;
      text-decoration: underline;

      &:hover {
        color: #003d80;
      }
    }
  }
}

.mc-file-input-help {
  font-size: 12px;
  color: #99a1b1;
  margin-top: 4px;
}


.mc-file-input-error {
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
}

/* Size adjustments */
.input-sm {
  .mc-file-input-container {
    height: 31px;
  }
  .mc-file-input-button,
  .mc-file-input-text {
    font-size: 12px;
  }
}

/* Preview Styles */
.mc-file-preview-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.mc-file-preview-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 100px;

  .mc-file-preview-name {
    color: #344264;
    font-size: 12px;
    margin: 0;
    word-break: break-all;
    text-align: center;
  }

  .mc-file-preview-img {
    border-radius: 4px;
    height: 100px;
    width: 100px;
    overflow: hidden;
    cursor: pointer;
    background-color: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #99a1b1;
    position: relative;

    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }

    .mc-file-preview-icon {
      i::before {
        font-size: 40px;
        color: #99a1b1;
      }
    }
  }

  .mc-file-preview-download {
    position: absolute;
    top: 3px;
    right: 3px;
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #0056b3;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s;

    &:hover {
      background-color: #f8f9fa;
    }

    i::before {
      font-size: 16px;
      color: #0056b3;
      font-weight: bold;
    }
  }
}
</style>
